(() => {
  if (customElements.get('ct-delivery-estimate')) return;

  class CtDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.hoverTrigger = this.querySelector(
        '[data-ct-delivery-hover-trigger]'
      );
      this.panel = this.querySelector(
        '[data-ct-delivery-panel]'
      );
      this.shippingMethod = this.querySelector(
        '[data-ct-shipping-method]'
      );
      this.closeTimer = null;

      this.renderDates();
      this.bindEvents();
    }

    bindEvents() {
      if (this.shippingMethod) {
        this.shippingMethod.addEventListener('change', () => {
          const selectedMethod = this.shippingMethod.value;

          document
            .querySelectorAll('ct-delivery-estimate')
            .forEach((estimate) => {
              if (
                estimate.dataset.countryCode !==
                this.dataset.countryCode
              ) {
                return;
              }

              const methodSelect = estimate.querySelector(
                '[data-ct-shipping-method]'
              );

              if (methodSelect) {
                methodSelect.value = selectedMethod;
              }

              if (typeof estimate.renderDates === 'function') {
                estimate.renderDates();
              }
            });
        });
      }

      if (!this.hoverTrigger || !this.panel) return;

      this.hoverTrigger.addEventListener('mouseenter', () => {
        if (!this.isHoverCapable()) return;
        this.cancelScheduledClose();
        this.open();
      });

      this.hoverTrigger.addEventListener('mouseleave', () => {
        if (!this.isHoverCapable()) return;
        this.scheduleClose();
      });

      this.panel.addEventListener('mouseenter', () => {
        if (!this.isHoverCapable()) return;
        this.cancelScheduledClose();
      });

      this.panel.addEventListener('mouseleave', () => {
        if (!this.isHoverCapable()) return;
        this.scheduleClose();
      });

      this.hoverTrigger.addEventListener('focus', () => {
        this.cancelScheduledClose();
        this.open();
      });

      this.hoverTrigger.addEventListener('blur', (event) => {
        if (this.contains(event.relatedTarget)) return;
        this.scheduleClose(0);
      });

      this.hoverTrigger.addEventListener('click', () => {
        if (this.isHoverCapable()) return;
        this.toggle();
      });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        this.close();
        this.hoverTrigger.focus();
      });

    }

    isHoverCapable() {
      return window.matchMedia(
        '(hover: hover) and (pointer: fine)'
      ).matches;
    }

    open() {
      this.cancelScheduledClose();
      this.panel.hidden = false;
      this.hoverTrigger.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
    }

    close() {
      this.cancelScheduledClose();
      this.panel.hidden = true;
      this.hoverTrigger.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
    }

    toggle() {
      const isOpen =
        this.hoverTrigger.getAttribute('aria-expanded') === 'true';

      if (isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    scheduleClose(delay = 160) {
      this.cancelScheduledClose();
      this.closeTimer = window.setTimeout(() => {
        this.close();
      }, delay);
    }

    cancelScheduledClose() {
      if (!this.closeTimer) return;
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    renderDates() {
      const today = this.startOfToday();
      const productionMinDays = this.numberFromData(
        'productionMinDays',
        9
      );
      const productionMaxDays = this.numberFromData(
        'productionMaxDays',
        10
      );
      const shippingMethod = this.shippingMethod?.value || 'standard';
      const isExpress =
        this.dataset.countryCode === 'US' &&
        shippingMethod === 'express';
      const shippingMinDays = this.numberFromData(
        isExpress
          ? 'expressShippingMinDays'
          : 'standardShippingMinDays',
        isExpress ? 1 : 6
      );
      const shippingMaxDays = this.numberFromData(
        isExpress
          ? 'expressShippingMaxDays'
          : 'standardShippingMaxDays',
        isExpress ? 2 : 10
      );

      const productionMin = this.addBusinessDays(
        today,
        productionMinDays
      );
      const productionMax = this.addBusinessDays(
        today,
        productionMaxDays
      );
      const deliveryMin = this.addBusinessDays(
        productionMin,
        shippingMinDays
      );
      const deliveryMax = this.addBusinessDays(
        productionMax,
        shippingMaxDays
      );

      const orderDateText = this.formatSingleDate(today);
      const productionRangeText = this.formatDateRange(
        productionMin,
        productionMax
      );
      const deliveryRangeText = this.formatDateRange(
        deliveryMin,
        deliveryMax
      );

      this.setText('[data-ct-order-date]', orderDateText);
      this.setText(
        '[data-ct-ship-range]',
        productionRangeText
      );
      this.setText(
        '[data-ct-delivery-range]',
        deliveryRangeText
      );
      this.setText(
        '[data-ct-delivery-range-panel]',
        deliveryRangeText
      );
    }

    numberFromData(key, fallback) {
      const value = Number.parseInt(this.dataset[key], 10);
      return Number.isFinite(value) ? value : fallback;
    }

    startOfToday() {
      const now = new Date();
      return new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate()
      );
    }

    addBusinessDays(date, numberOfDays) {
      const result = new Date(date);
      let remainingDays = Math.max(0, numberOfDays);

      while (remainingDays > 0) {
        result.setDate(result.getDate() + 1);

        const dayOfWeek = result.getDay();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

        if (!isWeekend) {
          remainingDays -= 1;
        }
      }

      return result;
    }

    get locale() {
      return (
        document.documentElement.lang ||
        navigator.language ||
        'en-US'
      );
    }

    formatSingleDate(date) {
      return new Intl.DateTimeFormat(this.locale, {
        month: 'short',
        day: 'numeric'
      }).format(date);
    }

    formatDateRange(start, end) {
      const formatter = new Intl.DateTimeFormat(this.locale, {
        month: 'short',
        day: 'numeric'
      });

      if (typeof formatter.formatRange === 'function') {
        return formatter.formatRange(start, end);
      }

      const sameYear =
        start.getFullYear() === end.getFullYear();
      const sameMonth =
        sameYear && start.getMonth() === end.getMonth();

      if (sameMonth) {
        const month = new Intl.DateTimeFormat(this.locale, {
          month: 'short'
        }).format(start);

        return `${month} ${start.getDate()} – ${end.getDate()}`;
      }

      return `${formatter.format(start)} – ${formatter.format(end)}`;
    }

    setText(selector, value) {
      this.querySelectorAll(selector).forEach((element) => {
        element.textContent = value;
      });
    }
  }

  customElements.define(
    'ct-delivery-estimate',
    CtDeliveryEstimate
  );
})();